import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { Plus, RefreshCw, Trash2, Edit, Truck } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';

/**
 * Suppliers page - manage Odoo suppliers for invoice syncing
 */
const Suppliers = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        odoo_partner_id: '',
        name: '',
        email: '',
        phone: '',
        active: true,
        name_cleaning_rule: ''
    });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        document.title = 'Fournisseurs | Costy';
        fetchSuppliers();
    }, []);

    const fetchSuppliers = async () => {
        try {
            const response = await axiosInstance.get('/suppliers/');
            const data = response.data;
            setSuppliers(Array.isArray(data) ? data : data?.results ?? []);
        } catch (error) {
            console.error('Failed to fetch suppliers:', error);
            setSuppliers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await axiosInstance.put(`/suppliers/${editingId}/`, formData);
            } else {
                await axiosInstance.post('/suppliers/', formData);
            }
            setShowForm(false);
            setFormData({ odoo_partner_id: '', name: '', email: '', phone: '', active: true, name_cleaning_rule: '' });
            setEditingId(null);
            fetchSuppliers();
        } catch (error) {
            console.error('Failed to save supplier:', error);
            alert('Enregistrement impossible.');
        }
    };

    const handleEdit = (supplier) => {
        setFormData({
            odoo_partner_id: supplier.odoo_partner_id,
            name: supplier.name,
            email: supplier.email || '',
            phone: supplier.phone || '',
            active: supplier.active,
            name_cleaning_rule: supplier.name_cleaning_rule || ''
        });
        setEditingId(supplier.id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!confirm('Supprimer ce fournisseur ?')) return;
        try {
            await axiosInstance.delete(`/suppliers/${id}/`);
            fetchSuppliers();
        } catch (error) {
            console.error('Failed to delete supplier:', error);
        }
    };

    const handleSync = async (supplierId) => {
        setSyncing(supplierId);
        try {
            const response = await axiosInstance.post(`/suppliers/${supplierId}/sync_invoices/`, {
                date_from: '2026-01-01'
            });
            alert(`${response.data.invoices_synced} factures importées, ${response.data.new_ingredients.length} nouveaux produits au catalogue`);
            fetchSuppliers();
        } catch (error) {
            console.error('Failed to sync invoices:', error);
            alert('Import impossible.');
        } finally {
            setSyncing(null);
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <PageHeader title="Fournisseurs" subtitle="Les fournisseurs dont Costy importe les factures depuis Odoo.">
                    <button
                        onClick={() => {
                            setFormData({ odoo_partner_id: '', name: '', email: '', phone: '', active: true, name_cleaning_rule: '' });
                            setEditingId(null);
                            setShowForm(true);
                        }}
                        className="btn-primary"
                    >
                        <Plus className="h-4 w-4" />
                        Ajouter
                    </button>
                </PageHeader>

                {/* Add/Edit Form Modal */}
                {showForm && (
                    <div className="sheet-overlay">
                        <div className="sheet sheet-sm">
                            <h2 className="text-2xl mb-4">
                                {editingId ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
                            </h2>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        ID partenaire Odoo *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.odoo_partner_id}
                                        onChange={(e) => setFormData({ ...formData, odoo_partner_id: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                        required
                                        placeholder="ex. 193154"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Nom *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                        required
                                        placeholder="Nom du fournisseur"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                        placeholder="fournisseur@exemple.be"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Téléphone
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                        placeholder="+32 xxx xxx xxx"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Règle de nettoyage du nom (regex)
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name_cleaning_rule}
                                        onChange={(e) => setFormData({ ...formData, name_cleaning_rule: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                        placeholder="\((?:\d{2}-?){3}\)\s*(.*)"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Extrait le nom du produit de la ligne de facture : le groupe 1 doit être le nom.
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="active"
                                        checked={formData.active}
                                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                        className="rounded border-border"
                                    />
                                    <label htmlFor="active" className="text-sm text-foreground">Actif</label>
                                </div>
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="btn-outline flex-1"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-primary flex-1"
                                    >
                                        Enregistrer
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Suppliers Table */}
                <div className="panel overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr>
                                <th className="px-4 py-3 text-left field-label">
                                    Fournisseur
                                </th>
                                <th className="hidden lg:table-cell px-4 py-3 text-left field-label">
                                    ID Odoo
                                </th>
                                <th className="hidden lg:table-cell px-4 py-3 text-left field-label">
                                    Ingrédients
                                </th>
                                <th className="hidden sm:table-cell px-4 py-3 text-left field-label">
                                    Statut
                                </th>
                                <th className="px-4 py-3 text-right field-label">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                                        Chargement…
                                    </td>
                                </tr>
                            ) : suppliers.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                                        <Truck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        <p>Aucun fournisseur pour l’instant.</p>
                                    </td>
                                </tr>
                            ) : (
                                suppliers.map((supplier) => (
                                    <tr key={supplier.id} className="hover:bg-muted/30 transition">
                                        <td className="px-4 py-4">
                                            <div>
                                                <p className="font-medium text-foreground">{supplier.name}</p>
                                                {supplier.email && (
                                                    <p className="text-sm text-muted-foreground break-all">{supplier.email}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="hidden lg:table-cell px-4 py-4 text-sm text-muted-foreground">
                                            {supplier.odoo_partner_id}
                                        </td>
                                        <td className="hidden lg:table-cell px-4 py-4 text-sm text-foreground">
                                            {supplier.ingredients_count}
                                        </td>
                                        <td className="hidden sm:table-cell px-4 py-4">
                                            <span className={`tag ${supplier.active ? 'text-good' : 'text-muted-foreground'}`}>
                                                {supplier.active ? 'actif' : 'inactif'}
                                            </span>
                                        </td>
                                        <td className="px-2 sm:px-4 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1 sm:gap-2">
                                                <button
                                                    onClick={() => handleSync(supplier.id)}
                                                    disabled={syncing === supplier.id}
                                                    className="p-2 text-foreground hover:bg-accent rounded-lg transition"
                                                    title="Importer les factures depuis Odoo" aria-label={`Importer les factures de ${supplier.name}`}
                                                >
                                                    <RefreshCw className={`h-4 w-4 ${syncing === supplier.id ? 'animate-spin' : ''}`} />
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(supplier)}
                                                    className="p-2 text-muted-foreground hover:bg-accent rounded-lg transition"
                                                    title="Modifier" aria-label={`Modifier ${supplier.name}`}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(supplier.id)}
                                                    className="p-2 text-bad hover:bg-accent rounded-full transition"
                                                    title="Supprimer" aria-label={`Supprimer ${supplier.name}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default Suppliers;
